/**
 * The one navigation, DECISION-018.
 *
 * The mockups render THREE different navigations across nine screens, including
 * a `Blueprints` / `Library` name collision and M2/M4/M6 items sitting in an M1
 * nav. DECISION-018 settled it: the `library` / `integrations` shell wins,
 * because it is the one that matches PRD §7.3–§7.6.
 *
 *   Work       Dashboard · Leads · Review queue · Funnels · Library
 *   Setup      Team · Integrations · Deliveries · Settings · Billing · Usage & cost
 *
 * WHAT IS DELIBERATELY ABSENT, and why each one:
 *
 *   · **Calibration** — not a nav item at all. PRD §7.5 puts the calibration
 *     bench inside the funnel editor's Qualify tab, and
 *     `design/screens/editor-qualify.html` draws it there. Two of the three
 *     mockup navs list it; both are wrong (DECISION-018).
 *   · **Dispatch queue** (M4) · **Analytics** (M6) · **Alerts** (M6) — they
 *     appear at their milestone, not before
 *     (DECISION-016, DECISION-018). A nav item that opens an empty page reads as
 *     broken rather than as not-yet-built.
 *
 * **Review queue arrived at its milestone, with its page** — Story 22.3, M2.
 * `tests/nav-inventory.test.ts` refuses a routed page that no nav reaches, so the
 * item and the page ship in one commit. It sits in Work between Leads and
 * Funnels, where `review-queue.html` draws it.
 *
 * **And it is offered only to the roles the API serves** — Story 22.5. PRD §7.4
 * gives the page to Reviewer, Admin and Owner and the API refuses everyone else,
 * so an Editor, a Viewer or a Client shown the item would be shown a page that
 * answers 403. `visibleTo` is a convenience, never an authority (RULE-009): the
 * server decides on every request, whatever this renders.
 *
 * **Usage & cost arrived the same way** — Story 23.3, M2, in Setup after Billing
 * where `usage-cost.html` draws it, and offered to Owner and Admin only, because
 * PRD §3.13 gives the page to them and the API refuses everyone else.
 *
 * THE SECTION LABELS come from `_system-sheet.html` §04, which is this story's
 * Visual Reference for the chrome — "Work" and "Setup". DECISION-018's phrase
 * "a Workspace group" is unbolded where the item names are bolded, so it reads
 * as describing which pages rather than naming the heading. The mockup names
 * the heading; taking its word invents nothing.
 *
 * Story 3.8.
 */
import {
  Blocks,
  CreditCard,
  LayoutDashboard,
  Library,
  ListChecks,
  Gauge,
  Plug,
  Send,
  Settings,
  Users,
  Waypoints,
  type LucideIcon,
} from 'lucide-react'

export type NavItem = {
  readonly label: string
  readonly path: string
  readonly icon: LucideIcon
  /**
   * Who is shown it, when that is not everyone in the workspace. Absent means
   * everyone. A convenience, never an authority — see the header.
   */
  readonly visibleTo?: (access: NavAccess) => boolean
}

/** What `/v1/me` says about the person, in the workspace the shell is pointed at. */
export type NavAccess = {
  /** `member.role` in that workspace's organisation — `owner`, `admin`, `member` — or none. */
  readonly orgRole: string | null
  /** The workspace role: an explicit grant's, or the org role an Owner or Admin reaches it by. */
  readonly workspaceRole: string | null
}

/**
 * Who works the review queue — Story 22.5.
 *
 * The API holds the route to `ws('reviewer', undefined, NOT_EDITOR)`, and
 * `services/authz`'s `hasRole` reads that as: an org Owner or Admin always, and
 * never excluded; a Reviewer by rank; an Editor by rank, then excluded; a Viewer
 * or a Client not at all. This is that rule, read off the two roles `/v1/me`
 * carries — so an Owner who also holds an Editor grant still sees it, as the API
 * still serves them. `tests/nav-inventory.test.ts` binds the two statements.
 */
export const canWorkTheQueue = ({ orgRole, workspaceRole }: NavAccess): boolean =>
  orgRole === 'owner' || orgRole === 'admin' || workspaceRole === 'reviewer'

/**
 * Who reads what the AI layer costs — Story 23.3.
 *
 * The API holds `GET /v1/orgs/{oid}/usage` to `org('admin')`: an org Owner or
 * Admin, and no workspace role reaches it, however senior. PRD §3.13 says "Owner
 * and Admin" in as many words. `tests/nav-inventory.test.ts` binds the two.
 */
export const canSeeUsage = ({ orgRole }: NavAccess): boolean => orgRole === 'owner' || orgRole === 'admin'

export type NavSection = {
  readonly label: string
  readonly items: readonly NavItem[]
}

export const NAV: readonly NavSection[] = [
  {
    label: 'Work',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
      { label: 'Leads', path: '/leads', icon: Waypoints },
      // Stories 22.3 and 22.5 — see the header.
      { label: 'Review queue', path: '/review-queue', icon: ListChecks, visibleTo: canWorkTheQueue },
      { label: 'Funnels', path: '/funnels', icon: Blocks },
      { label: 'Library', path: '/library', icon: Library },
    ],
  },
  {
    label: 'Setup',
    items: [
      { label: 'Team', path: '/team', icon: Users },
      { label: 'Integrations', path: '/integrations', icon: Plug },
      /**
       * THE NINTH, and DECISION-087 amends DECISION-018 to add it.
       *
       * In Setup rather than Work, beside the thing that produces it: an
       * integration is what sends, and `integrations.html`'s own
       * `See all deliveries` link is the reason this destination exists at all
       * (Q-063). Work's items are the lead pipeline — where leads come from and
       * where they go — and a log of outbound sends is not one of them.
       *
       * NOT "Dispatch queue", which `NOT_YET` below still reserves for M4:
       * that is the retry surface with a Retry button on it, and this is the
       * read-only log Story 9.5 built.
       */
      { label: 'Deliveries', path: '/deliveries', icon: Send },
      { label: 'Settings', path: '/settings', icon: Settings },
      { label: 'Billing', path: '/billing', icon: CreditCard },
      // Story 23.3 — see the header.
      { label: 'Usage & cost', path: '/usage', icon: Gauge, visibleTo: canSeeUsage },
    ],
  },
]

/** Every navigable path, flattened — used by the router and by the tests. */
export const NAV_PATHS: readonly string[] = NAV.flatMap((s) => s.items.map((i) => i.path))

/** The navigation one person is shown: `NAV`, less what is not theirs to open. */
export function navFor(access: NavAccess): readonly NavSection[] {
  return NAV.map((section) => ({
    ...section,
    items: section.items.filter((item) => item.visibleTo?.(access) ?? true),
  }))
}

/** Where a signed-in person lands. PRD §6.5 calls it a start-of-day instrument. */
export const HOME_PATH = '/dashboard'

/**
 * Items that must NOT render before their milestone, asserted by
 * `e2e/app-shell.spec.ts` and `tests/nav-inventory.test.ts`.
 *
 * Written down rather than merely omitted: an absence proves nothing on its
 * own, and the failure DECISION-016 guards against is somebody adding one back
 * because a mockup shows it. `Review queue` left this list with Story 22.3, and
 * `Usage & cost` with Story 23.3, each in the commit that gave it a page.
 */
export const NOT_YET: readonly string[] = [
  'Calibration',
  'Dispatch queue',
  'Dispatch',
  'Analytics',
  'Alerts',
]
